'use client';

import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Button } from '@/components/ui/button';
import { Heart, Bone, Smile, Shield, TrendingUp, ArrowRight, ArrowLeft, Speech } from 'lucide-react';
import Image from 'next/image';
import { useTranslation } from '@/hooks/useTranslation';
import { useRTL } from '@/hooks/useRTL';
import { useLocalizedNumbers } from '@/hooks/useLocalizedNumbers';
import { cn } from '@/lib/utils';

gsap.registerPlugin(ScrollTrigger);

export default function WhyChooseImplantsSection() {
    const { t } = useTranslation('dental_implants');
    const { isRTL } = useRTL();
    const { localizeText } = useLocalizedNumbers();
    const sectionRef = useRef<HTMLElement>(null);
    const cardsRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLDivElement>(null);

    const benefits = [
        {
            icon: <Heart className="w-8 h-8 text-gold" />,
            title: localizeText(t('why_choose.benefits.lasting.title')),
            description: localizeText(t('why_choose.benefits.lasting.description'))
        },
        {
            icon: <Shield className="w-8 h-8 text-gold" />,
            title: localizeText(t('why_choose.benefits.no_damage.title')),
            description: localizeText(t('why_choose.benefits.no_damage.description'))
        },
        {
            icon: <Bone className="w-8 h-8 text-gold" />,
            title: localizeText(t('why_choose.benefits.bone_health.title')),
            description: localizeText(t('why_choose.benefits.bone_health.description'))
        },
        {
            icon: <Smile className="w-8 h-8 text-gold" />,
            title: localizeText(t('why_choose.benefits.comfortable.title')),
            description: localizeText(t('why_choose.benefits.comfortable.description'))
        },
        {
            icon: <TrendingUp className="w-8 h-8 text-gold" />,
            title: localizeText(t('why_choose.benefits.self_esteem.title')),
            description: localizeText(t('why_choose.benefits.self_esteem.description'))
        },
        {
            icon: <Speech className="w-8 h-8 text-gold" />,
            title: localizeText(t('why_choose.benefits.speech.title')),
            description: localizeText(t('why_choose.benefits.speech.description'))
        }
    ];

    useEffect(() => {
        const ctx = gsap.context(() => {
            // Fade-in animation for each benefit card
            if (cardsRef.current?.children) {
                gsap.fromTo(cardsRef.current.children,
                    { opacity: 0, y: 50, scale: 0.9 },
                    {
                        opacity: 1,
                        y: 0,
                        scale: 1,
                        duration: 0.8,
                        stagger: 0.15,
                        ease: "power3.out",
                        scrollTrigger: {
                            trigger: cardsRef.current,
                            start: "top 80%",
                            end: "bottom 20%",
                            toggleActions: "play none none reverse"
                        }
                    }
                );
            }

            // Image animation
            if (imageRef.current) {
                gsap.fromTo(imageRef.current,
                    { opacity: 0, x: 50 },
                    {
                        opacity: 1,
                        x: 0,
                        duration: 1,
                        ease: "power3.out",
                        scrollTrigger: {
                            trigger: imageRef.current,
                            start: "top 85%",
                            toggleActions: "play none none reverse"
                        }
                    }
                );
            }
        }, sectionRef);

        return () => ctx.revert();
    }, []);

    return (
        <section
            id="why-implants"
            ref={sectionRef}
            className="py-20 bg-white dark:bg-slate-800"
        >
            <div className="container">
                <div className="text-center mb-16">
                    <h2 className={cn(
                        "text-4xl md:text-5xl font-bold text-navy dark:text-white mb-6 font-serif",
                        isRTL ? "font-arabic" : ""
                    )}>
                        {localizeText(t('why_choose.title_part1'))}{' '}
                        <span className="text-gold">{localizeText(t('why_choose.title_part2'))}</span>{' '}
                        {localizeText(t('why_choose.title_part3'))}
                    </h2>
                    {/* Implant Structure Image */}
                    <div className="mb-20">
                        <div ref={imageRef} className="flex justify-center">
                            <div className="relative max-w-xl w-full">
                                <Image
                                    src="/images/treatments/implant/dental-impalent-structure.webp"
                                    alt={localizeText(t('why_choose.image_alt'))}
                                    width={400}
                                    height={500}
                                    className="w-full h-auto rounded-2xl shadow-xl"
                                    priority
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl pointer-events-none"></div>
                            </div>
                        </div>
                    </div>
                    <div className="max-w-4xl mx-auto">
                        <p className={cn(
                            "text-lg md:text-xl text-slate-600 dark:text-slate-300 leading-relaxed mb-8",
                            isRTL ? "font-arabic" : ""
                        )}>
                            {localizeText(t('why_choose.description_1'))}
                        </p>
                        <p className={cn(
                            "text-lg text-slate-600 dark:text-slate-300 leading-relaxed",
                            isRTL ? "font-arabic" : ""
                        )}>
                            {localizeText(t('why_choose.description_2'))}
                        </p>
                    </div>
                </div>

                {/* Benefits Grid */}
                <div ref={cardsRef} className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                    {benefits.map((benefit, index) => (
                        <div
                            key={index}
                            className="group bg-white dark:bg-slate-800 rounded-2xl p-8 shadow-lg dark:shadow-none dark:border dark:border-slate-700 hover:shadow-2xl dark:hover:border-gold/30 transition-all duration-300 transform hover:-translate-y-2"
                        >
                            <div className="flex flex-col items-center text-center space-y-4">
                                <div className="p-4 rounded-xl bg-gold/10 group-hover:bg-gold/20 transition-colors duration-300">
                                    {benefit.icon}
                                </div>
                                <h3 className={cn(
                                    "text-xl font-semibold text-navy dark:text-white group-hover:text-gold transition-colors",
                                    isRTL ? "font-arabic" : ""
                                )}>
                                    {benefit.title}
                                </h3>
                                <p className={cn(
                                    "text-slate-600 dark:text-slate-300 leading-relaxed",
                                    isRTL ? "font-arabic" : ""
                                )}>
                                    {benefit.description}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>

                {/* CTA Section */}
                <div className="text-center">
                    <Button
                        variant="outline"
                        size="lg"
                        className={cn(
                            "border-2 border-gold text-gold hover:bg-gold hover:text-navy px-8 py-4 rounded-xl transition-all duration-300",
                            isRTL ? "font-arabic" : ""
                        )}
                    >
                        {localizeText(t('why_choose.cta_button'))}
                        {isRTL ? (
                            <ArrowLeft className="w-5 h-5 mr-2" />
                        ) : (
                            <ArrowRight className="w-5 h-5 ml-2" />
                        )}
                    </Button>
                </div>
            </div>
        </section>
    );
} 
